import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchAnalytics } from '../../store/slices/adminSlice';
import { Box, Typography, Grid, Card, CardContent, CircularProgress, Paper } from '@mui/material';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler);

const AdminAnalytics = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { analytics, loading } = useSelector(state => state.admin);

  useEffect(() => { dispatch(fetchAnalytics()); }, [dispatch]);

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  const ordersTrend = {
    labels: analytics?.ordersTrend?.map(d => d.date) || Array.from({ length: 30 }, (_, i) => `Day ${i + 1}`),
    datasets: [{
      label: 'Orders', data: analytics?.ordersTrend?.map(d => d.count) || Array(30).fill(0),
      borderColor: '#1a237e', backgroundColor: 'rgba(26,35,126,0.1)', fill: true, tension: 0.4
    }]
  };

  const revenueTrend = {
    labels: analytics?.revenueTrend?.map(d => d.date) || Array.from({ length: 30 }, (_, i) => `Day ${i + 1}`),
    datasets: [{
      label: 'Revenue ($)', data: analytics?.revenueTrend?.map(d => d.amount) || Array(30).fill(0),
      backgroundColor: '#ff6f00'
    }]
  };

  const userDistribution = {
    labels: ['Customers', 'Suppliers', 'Admins'],
    datasets: [{
      data: [
        analytics?.userStats?.customers || 0,
        analytics?.userStats?.suppliers || 0,
        analytics?.userStats?.admins || 0
      ],
      backgroundColor: ['#2e7d32', '#1565c0', '#c62828']
    }]
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>{t('analytics')}</Typography>

      <Grid container spacing={3}>
        {/* Orders Trend */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Orders (Last 30 Days)</Typography>
            <Line data={ordersTrend} options={{ responsive: true, plugins: { legend: { display: false } } }} />
          </Paper>
        </Grid>

        {/* User Distribution */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>User Distribution</Typography>
            <Doughnut data={userDistribution} />
          </Paper>
        </Grid>

        {/* Revenue */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Revenue (Last 30 Days)</Typography>
            <Bar data={revenueTrend} options={{ responsive: true, plugins: { legend: { display: false } } }} />
          </Paper>
        </Grid>

        {/* Summary Cards */}
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: '#e8eaf6' }}>
            <CardContent>
              <Typography variant="h4" fontWeight={700} color="primary">
                {analytics?.totalOrders30d || 0}
              </Typography>
              <Typography variant="body2">Orders This Month</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Typography variant="h4" fontWeight={700} color="secondary">
                ${(analytics?.totalRevenue30d || 0).toLocaleString()}
              </Typography>
              <Typography variant="body2">Revenue This Month</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#2e7d32' }}>
                {analytics?.newUsers30d || 0}
              </Typography>
              <Typography variant="body2">New Users This Month</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminAnalytics;
